import { PieChart, Pie, Cell, Label, Sector } from 'recharts'
import { ChartContainer, ChartTooltip } from '@/components/ui/chart'
import { ChartSkeleton } from './ChartSkeleton'
import { ChartError } from './ChartError'
import { EmptyChart } from './EmptyChart'
import { usePacketsByDecision } from '../hooks/usePacketsByDecision'
import { Decision } from '@promo/shared'

export function PacketsByDecisionChart({ className }: { className?: string }) {
  const { data, isLoading, error } = usePacketsByDecision()

  if (isLoading) return <ChartSkeleton className={className} />
  if (error) return <ChartError className={className} />
  if (!data?.length) return <EmptyChart message="No packets by decision data available" className={className} />

  const total = data.reduce((sum, d) => sum + d.count, 0)

  const chartConfig = {
    promote: {
      label: 'Promote',
      color: 'hsl(var(--chart-1))',
    },
    defer: {
      label: 'Defer',
      color: 'hsl(var(--chart-2))',
    },
    pending: {
      label: 'Pending',
      color: 'hsl(var(--chart-3))',
    },
  }

  // Map data to match expected format
  const chartData = data.map((item) => {
    let name, fill;
    if (item.decision === Decision.PROMOTE) {
      name = chartConfig.promote.label;
      fill = chartConfig.promote.color;
    } else if (item.decision === Decision.DEFER) {
      name = chartConfig.defer.label;
      fill = chartConfig.defer.color;
    } else {
      name = chartConfig.pending.label;
      fill = chartConfig.pending.color;
    }
    return { name, value: item.count, fill };
  });

  // Find the index of the slice with the highest value (majority slice)
  const activeIndex = chartData.reduce(
    (maxIdx, current, idx, arr) => current.value > arr[maxIdx].value ? idx : maxIdx, 0
  )

  return (
    <div className={className}>
      <div className="mb-4">
        <h3 className="text-xl font-semibold">Packets by Decision</h3>
        <p className="text-base text-muted-foreground">Current year packets grouped by decision</p>
      </div>
      <ChartContainer
        config={chartConfig}
        className="mx-auto aspect-square w-full max-w-[300px] h-[300px]"
      >
        <PieChart>
          <ChartTooltip
            cursor={false}
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                const data = payload[0];
                return (
                  <div className="rounded-lg border bg-background p-2 shadow-md">
                    <div className="flex items-center gap-2">
                      <div 
                        className="h-2 w-2 rounded-sm" 
                        style={{ backgroundColor: data.payload.fill }}
                      />
                      <span className="text-xs">
                        {data.name}
                      </span>
                      <span className="text-xs font-semibold ml-1">
                        {data.value}
                      </span>
                    </div>
                  </div>
                );
              }
              return null;
            }}
          />
          <Pie
            data={chartData}
            dataKey="value"
            nameKey="name"
            innerRadius={80}
            outerRadius={120}
            strokeWidth={5}
            paddingAngle={data.length > 1 ? 5 : 0}
            activeIndex={activeIndex}
            activeShape={({ outerRadius = 0, ...props }) => (
              <Sector {...props} outerRadius={outerRadius + 15} />
            )}
          >
            {chartData.map((entry, idx) => (
              <Cell key={`cell-${idx}`} fill={entry.fill} />
            ))}
            <Label
              content={({ viewBox }) => {
                if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                  return (
                    <text
                      x={viewBox.cx}
                      y={viewBox.cy}
                      textAnchor="middle"
                      dominantBaseline="middle"
                    >
                      <tspan
                        x={viewBox.cx}
                        y={viewBox.cy}
                        className="fill-foreground text-7xl font-bold"
                      >
                        {total.toLocaleString()}
                      </tspan>
                      <tspan
                        x={viewBox.cx}
                        y={(viewBox.cy || 0) + 40}
                        className="fill-muted-foreground text-sm"
                      >
                        DECISIONS
                      </tspan>
                    </text>
                  )
                }
                return null
              }}
              position="center"
            />
          </Pie>
        </PieChart>
      </ChartContainer>
    </div>
  )
} 